import {
  inject,
  signal,
  computed,
  Component,
  ViewChild,
  DestroyRef,
  ViewContainerRef,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
  SuiDialogConfig,
  DialogCloseReason,
  DialogCloseOptions,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';

import { LoggerService } from '@services/logger.service';
import { WidgetApiService } from 'src/app/api/services/widget-api.service';
import { WidgetSettingsService } from 'src/app/widgets/services/widget.settings.service';

import { AuditTrailComponent } from 'src/app/shared/components/audit-trail/audit-trail.component';
import { WidgetSettingsGeneralComponent } from './widget-settings-general/widget-settings-general.component';
import { WidgetSettingsChartAxesComponent } from './widget-settings-chart-axes/widget-settings-chart-axes.component';
import { WidgetSettingsDataSeriesComponent } from './widget-settings-data-series/widget-settings-data-series.component';
import { WidgetSettingsChartLayoutComponent } from './widget-settings-chart-layout/widget-settings-chart-layout.component';
import { WidgetSettingsOutputSettingsComponent } from './widget-settings-output-settings/widget-settings-output-settings.component';
import { WidgetSettingsDebugChartJsonComponent } from './widget-settings-debug-chart-json/widget-settings-debug-chart-json.component';
import { WidgetSettingsDebugChartConfigComponent } from './widget-settings-debug-chart-config/widget-settings-debug-chart-config.component';

import {
  WidgetVizType,
  WidgetVizTypeChange,
  chartTypeIdToVisualizationTypeMap,
} from 'src/app/core/models/widget.models';
import { ContentFilters, Widget } from 'src/app/core/models/core.models';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';
import { TabConfig, SearchableDialog } from 'src/app/shared/utils';

import { injectWidgetVizTypesQuery } from 'src/app/api/queries/widget.queries';
import { WidgetPersistenceService } from 'src/app/core/services/widget-persistence.service';

import {
  getContentFilters,
  selectWidgetsLanguageId,
  selectCurrentObjectCacheEnabled,
  selectUser,
} from 'src/app/core/state';
import { AppState } from 'src/app/core/state/app.state';

import {
  of,
  tap,
  take,
  EMPTY,
  filter,
  Subject,
  switchMap,
  catchError,
  Observable,
  debounceTime,
  combineLatest,
  ReplaySubject,
  withLatestFrom,
  firstValueFrom,
} from 'rxjs';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import { NomLanguages } from 'src/app/core/constants';
import { WidgetSettingsContentViewPropertiesComponent } from './widget-settings-content-view-properties/widget-settings-content-view-properties.component';
import {
  FeedSettingsService,
  PropertyViewConfig,
} from 'src/app/core/services/feed-settings.service';
import { NomObjectTypes } from 'src/app/core/constants';
import { Actions, ofType } from '@ngrx/effects';
import { editFeedSuccess } from 'src/app/core/state/feed.actions';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { DialogTabNavComponent } from '../../../shared/components/dialog-tab-nav/dialog-tab-nav.component';
import { SuiButtonComponent } from '@sui/button';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { WidgetVisualizationTypeCardComponent } from '../../components/widget-visualization-type-card/widget-visualization-type-card.component';
import { WidgetCardComponent } from '../../components/widget-card/widget-card.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

interface WidgetDialogInput extends TabDialogDataBase {
  widget?: Widget;
  tab?: string;
  massUpdate?: boolean;
  widgets?: Widget[];
}

interface WidgetDialogOutput {}

@Component({
  selector: 'app-widget-settings-dialog',
  templateUrl: './widget-settings-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './widget-settings-dialog.component.scss',
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    SearchInputComponent,
    DialogTabNavComponent,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    LoadingComponent,
    WidgetVisualizationTypeCardComponent,
    forwardRef(() => WidgetCardComponent),
    SuiDialogRightFooterSlot,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class WidgetSettingsDialogComponent
  extends TabDialogBase<WidgetDialogInput, WidgetDialogOutput>
  implements SearchableDialog
{
  static dialogConfig: Partial<SuiDialogConfig> = {
    width: '80vw',
    minWidth: 1536,
    maxWidth: 1920,
  };
  private logger = inject(LoggerService);
  private store = inject(Store<AppState>);
  private readonly widgetPersistence = inject(WidgetPersistenceService);
  protected destroyRef = inject(DestroyRef);
  private widgetApi = inject(WidgetApiService);
  private widgetSettings = inject(WidgetSettingsService);
  private actions$ = inject(Actions);
  private readonly translate = inject(TranslateService);

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  // Keys that should NOT trigger a preview update
  IGNORED_KEYS = [
    'title',
    'widget_annotation',
    'icon_uri',
    'settings.feed.view_properties',
    'settings.export_title.language_id_' + NomLanguages.BULGARIAN,
    'settings.export_title.language_id_' + NomLanguages.ENGLISH,
    'settings.export_title.language_id_' + NomLanguages.ARABIC,
  ];

  SPECIAL_TRIGGER_KEYS = ['settings.feed.view_properties'];

  // * UI state
  objectActionTitle = '';
  objectNameTitle = '';
  objectIcon: IconProp = ['fas', 'shapes'];
  objectActionIcon: IconProp = ['fas', 'cog'];

  // * Set by loadVizTypes() once the widget's chart is known.
  private readonly vizTypesChartId = signal<number | null>(null);
  private readonly vizTypesQuery = injectWidgetVizTypesQuery(
    this.vizTypesChartId,
  );
  public vizTypes$: Observable<WidgetVizType[]> = toObservable(
    computed(() => this.vizTypesQuery.data()),
  );
  public contentFilters$ = new Observable<ContentFilters>();
  public vizTypeChange$ = new Subject<WidgetVizTypeChange>();

  postCardViewConfig: PropertyViewConfig[];

  private widgetPreviewSubject = new Subject<any>();
  private popupViewInit = new ReplaySubject<boolean>(1);

  private latestZoom: number = 1.5;
  private latestCenter: [number, number] = [0, 0];
  private settingWidget: boolean = false;
  private feedSettings = inject(FeedSettingsService);
  formGroup: FormGroup;
  contentFilters: ContentFilters;
  lastFormData: any;
  loadingWidgetPreview = false;

  set widget(widget: Widget) {
    if (this.settingWidget) return;
    this.settingWidget = true;
    this.dialogData.widget = widget;
    this.settingWidget = false;
  }

  get widget() {
    return this.dialogData.widget;
  }

  get widgets() {
    return this.dialogData.widgets;
  }

  get tab() {
    return this.dialogData.tab;
  }

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  ngOnInit(): void {
    this.setupUIState();
    this.setupTabs();
    if (this.widget && !this.dialogData.massUpdate) {
      this.addChartTypeIdFormGroup();
      this.loadVizTypes();
    }
    this.loadContentFilters();

    // * Combine popupViewInit and inputChanged observables
    combineLatest([
      this.popupViewInit.pipe(filter((init) => init === true)),
      this.tabManager.activeFormValueChanges$.pipe(debounceTime(300)),
    ])
      .pipe(
        tap(([, values]) => {
          if (this.lastFormData && values) {
            // Check if any "preview-relevant" properties actually changed
            // Identify what actually changed;
            // we don't want to trigger preview if only "ignored" properties changed (like title or annotation)

            const activeTabId = this.tabManager.activeTabId();

            const changedKeys = Object.keys(values).filter(
              (key) => values[key] !== this.lastFormData[key],
            );
            if (changedKeys.length === 0) return;

            const hasSpecialChanges = changedKeys.some((key) =>
              this.SPECIAL_TRIGGER_KEYS.includes(key),
            );
            if (hasSpecialChanges) {
              this.onSpecialPropertyChanged(values, changedKeys);
            }

            const onlyIgnoredChanged = changedKeys.every((key) =>
              this.IGNORED_KEYS.includes(key),
            );
            if (onlyIgnoredChanged || activeTabId === 'export_and_share') {
              this.lastFormData = { ...values };
              return;
            }

            const hasPreviewChanges = changedKeys.some(
              (key) => !this.IGNORED_KEYS.includes(key),
            );
            if (!hasPreviewChanges) return;
          }
          this.lastFormData = this.tabManager.combineValues();

          this.logger.debug(
            'WidgetSettingsDialogComponent',
            'combineLatest',
            'viewInit and inputChanged',
          );
          this.onWidgetSettingsInputChanged();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.widgetPreviewSubject
      .pipe(
        debounceTime(1000),
        withLatestFrom(
          this.store.select(selectWidgetsLanguageId),
          this.store.select(selectCurrentObjectCacheEnabled),
        ),
        tap(() => {
          this.loadingWidgetPreview = true;
        }),
        switchMap(([widgetCopy, widgetsLanguageId, cacheEnabled]) =>
          this.widgetApi
            .getWidgetPreview(
              widgetCopy,
              this.contentFilters,
              widgetsLanguageId,
              cacheEnabled,
            )
            .pipe(
              catchError((error) => {
                console.error(error);
                this.presenter.present(error, {
                  fallback: this.translate.instant(
                    'widget-settings.toast.preview-could-not-be-loaded',
                  ),
                });
                this.loadingWidgetPreview = false;
                return of(null);
              }),
            ),
        ),
        tap((widgetPreview) => {
          if (widgetPreview) {
            this.widget = { ...this.widget, data: widgetPreview };
          }
          this.loadingWidgetPreview = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(editFeedSuccess),
        tap(() => {
          this.onWidgetSettingsInputChanged();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private onSpecialPropertyChanged(values: any, changedKeys: string[]): void {
    if (changedKeys.includes('settings.feed.view_properties')) {
      this.estimatePostViewConfig(values);
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    this.tabSearch.setupAutoSwitch(this.tabs);
    this.popupViewInit.next(true);

    if (this.dialogData.massUpdate) {
      // can filter tabs based on list of available properties across multiple widgets
      setTimeout(() => {
        this.tabs = this.tabManager.filterTabsByAvailableActiveFormProperties(
          this.tabs,
        );
        this.tabManager.selectTab(this.tabs[0].id);
      }, 0);
    }
  }

  private setupUIState(): void {
    if (this.widget) {
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.edit-widget-settings',
      );
      this.objectNameTitle = this.widget.title;
    }

    if (this.dialogData.massUpdate) {
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.mass-update-widget-settings',
      );
      this.objectNameTitle = this.translateService.instant(
        'widget-settings-popup.mass-update.multiple-widgets',
        {
          count: this.widgets.length,
        },
      );
      this.objectIcon = ['fas', 'layer-group'];
    }
  }

  private setupTabs(): void {
    const massUpdate = !!this.dialogData?.massUpdate;

    this.tabs = [
      {
        label: this.translateService.instant(
          'widget-settings-popup.tab.general',
        ),
        component: WidgetSettingsGeneralComponent,
        id: 'widget_general',
        inputs: {
          widget: this.widget,
          searchString$: this.searchString$,
          vizTypeChange$: this.vizTypeChange$,
          tab: this.tab,
          massUpdate,
          widgets: this.widgets,
        },
      },
      {
        label: this.translateService.instant(
          'widget-settings-popup.tab.data-series',
        ),
        component: WidgetSettingsDataSeriesComponent,
        id: 'chart_data_series',
        inputs: {
          widget: this.widget,
          searchString$: this.searchString$,
          vizTypeChange$: this.vizTypeChange$,
          massUpdate,
          widgets: this.widgets,
        },
      },
      {
        label: this.translateService.instant(
          'widget-settings-popup.tab.chart-layout',
        ),
        component: WidgetSettingsChartLayoutComponent,
        id: 'chart_layout',
        inputs: {
          widget: this.widget,
          searchString$: this.searchString$,
          vizTypeChange$: this.vizTypeChange$,
          massUpdate,
          widgets: this.widgets,
        },
      },
      {
        label: this.translateService.instant(
          'widget-settings-popup.tab.chart-axes',
        ),
        component: WidgetSettingsChartAxesComponent,
        id: 'chart_axes',
        inputs: {
          widget: this.widget,
          searchString$: this.searchString$,
          vizTypeChange$: this.vizTypeChange$,
          massUpdate,
          widgets: this.widgets,
        },
      },

      {
        label: this.translateService.instant(
          'widget-settings-popup.tab.view-settings',
        ),
        component: WidgetSettingsContentViewPropertiesComponent,
        id: 'content_view_properties',
        inputs: {
          widget: this.widget,
          searchString$: this.searchString$,
          vizTypeChange$: this.vizTypeChange$,
          massUpdate,
          widgets: this.widgets,
        },
      },
      {
        label: this.translateService.instant(
          'widget-settings-popup.tab.export-and-share',
        ),
        component: WidgetSettingsOutputSettingsComponent,
        id: 'export_and_share',
        inputs: {
          widget: this.widget,
          searchString$: this.searchString$,
          vizTypeChange$: this.vizTypeChange$,
          massUpdate,
          widgets: this.widgets,
        },
      },
    ];

    if (!massUpdate) {
      this.tabs.push(
        {
          label: this.translateService.instant(
            'widget-settings-popup.tab.audit-trail',
          ),
          component: AuditTrailComponent,
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          id: 'object_audit_trail',
          inputs: {
            searchString$: this.searchString$,
            object: this.widget,
            hasOwner: false,
            hasCharts: true,
            timeCreatedKey: 'time_created.absolute',
            timeModifiedKey: 'time_modified.absolute',
          },
        },
        {
          label: this.translateService.instant(
            'widget-settings-popup.tab.debug-chart-config',
          ),
          component: WidgetSettingsDebugChartConfigComponent,
          id: 'debug_chart_config',
          userAccessLevel: UserAccessLevel.PLATFORM_ADMIN,
          inputs: { widget: this.widget },
        },
        {
          label: this.translateService.instant(
            'widget-settings-popup.tab.debug-chart-json',
          ),
          component: WidgetSettingsDebugChartJsonComponent,
          id: 'debug_chart_json',
          userAccessLevel: UserAccessLevel.PLATFORM_ADMIN,
          inputs: { widget: this.widget },
        },
      );
    }

    if (this.widget && !this.dialogData.massUpdate) {
      const widgetTypeId = this.widget.widget_type_id;
      const chartTypeId = this.widget.settings.chart_type_id;
      const vizTypeId = chartTypeIdToVisualizationTypeMap.get(chartTypeId);

      // filter tabs based on Chart Type and VizType
      this.tabs = this.tabs.filter((tab) =>
        this.widgetSettings.hasProperty(
          tab.id,
          widgetTypeId,
          vizTypeId,
          chartTypeId,
        ),
      );
    }

    // filter tabs based on User Access Level
    this.store
      .select(selectUser)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((user) => this.filterTabsByObjectPermissions(this.widget, user)),
      )
      .subscribe();
  }

  addChartTypeIdFormGroup() {
    this.formGroup = new FormGroup({
      'settings.chart_type_id': new FormControl(
        this.widget.settings.chart_type_id,
      ),
    });

    this.tabManager.addFormGroup(this.formGroup);
  }

  loadContentFilters() {
    this.contentFilters$ = this.store.select(getContentFilters);
    this.contentFilters$
      .pipe(
        // side-effect: update the local contentFilters property
        tap((filters) => (this.contentFilters = filters)),
        // automatically unsubscribe when the component is destroyed
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  loadVizTypes() {
    // * the query fetches as soon as the id is set, and stays disabled for 0/null
    this.vizTypesChartId.set(this.widget.chart?.id ?? null);
  }

  async onWidgetSettingsInputChanged(): Promise<void> {
    this.logger.debug(
      'WidgetSettingsDialogComponent',
      'previewWidget',
      'preview',
    );

    const allFormData = this.tabManager.combineValues();

    await this.estimatePostViewConfig(allFormData);

    const widgetCopy = this.dialogData.massUpdate
      ? {}
      : structuredClone(this.widget); // modern deep clone

    this.populateWidget(widgetCopy, allFormData);

    if (!this.dialogData.massUpdate) {
      this.widgetPreviewSubject.next(widgetCopy);
    }
  }

  private async estimatePostViewConfig(allFormData: Record<string, any>) {
    const user = await firstValueFrom(this.store.select(selectUser));

    let objectTypeId: number = this.widget?.datasources?.[0]?.object_type_id;

    // If it's a mass update and the primary ID is missing, find the first available one in the list
    if (this.dialogData.massUpdate) {
      objectTypeId = this.dialogData.widgets?.find(
        (w) => w?.datasources?.[0]?.object_type_id !== undefined,
      )?.datasources?.[0]?.object_type_id;
    }

    // Fallback to a default if absolutely nothing was found
    objectTypeId ??= NomObjectTypes.ONLINE_FEED;

    const feedConfig = await this.feedSettings.getFeedConfig(
      objectTypeId,
      user,
    );

    // Get the system defaults from feedConfig
    const defaultSelected = feedConfig
      .filter((c) => c.selected)
      .map((c) => c.path);

    //  Extract the current form value
    const formValue = allFormData['settings.feed.view_properties'];

    /** * LOGIC:
     * Use defaultSelected if:
     * - The key doesn't exist in allFormData
     * - The value is null/undefined
     * - The value is an empty array (meaning the user or form initialized it but nothing is checked)
     */
    const selectedPaths =
      formValue && formValue.length > 0 ? formValue : defaultSelected;

    this.postCardViewConfig =
      this.feedSettings.buildViewConfigFromSelectedPaths(
        feedConfig,
        selectedPaths,
      );
  }

  populateWidget(widget: any, formDataDotNotation: Record<string, any>) {
    Object.keys(formDataDotNotation).forEach((key) => {
      const properties = key.split('.');
      let current: any = widget;

      for (let i = 0; i < properties.length; i++) {
        const prop = properties[i];
        const isLast = i === properties.length - 1;
        const isNumeric = /^\d+$/.test(prop); // detect numeric key

        if (isLast) {
          // last: assign value
          if (isNumeric) {
            if (!Array.isArray(current)) current = [];
            current[+prop] = formDataDotNotation[key];
          } else {
            current[prop] = formDataDotNotation[key];
          }
        } else {
          // not last: ensure intermediate container exists
          const nextProp = properties[i + 1];
          const nextIsNumeric = /^\d+$/.test(nextProp);

          if (isNumeric) {
            if (!Array.isArray(current)) {
              // replace with array if not already
              current = [];
            }
            if (!current[+prop]) {
              current[+prop] = nextIsNumeric ? [] : {};
            }
            current = current[+prop];
          } else {
            if (typeof current[prop] !== 'object' || current[prop] === null) {
              current[prop] = nextIsNumeric ? [] : {};
            }
            current = current[prop];
          }
        }
      }
    });
  }

  onZoomChanged(event: { zoom: number; center: [number, number] }) {
    this.latestZoom = event.zoom;
    this.latestCenter = event.center;
  }

  get chartTypeId() {
    return this.formGroup.get(['settings.chart_type_id']).value;
  }

  set chartTypeId(value) {
    this.formGroup.get(['settings.chart_type_id']).setValue(value);
  }

  selectVizType(vizType: WidgetVizType) {
    const widgetCopy = JSON.parse(JSON.stringify(this.widget));
    const allFormData = this.tabManager.combineValues();

    this.populateWidget(widgetCopy, allFormData);
    this.chartTypeId = vizType.id;

    widgetCopy.chart_type = vizType;
    widgetCopy.settings.chart_type_id = vizType.id;
    widgetCopy.widget_type_id = vizType.widget_type_id;

    this.vizTypeChange$.next({
      widgetTypeId: this.widget.widget_type_id,
      charTypeId: vizType.id,
    });

    this.loadingWidgetPreview = true;

    of(widgetCopy)
      .pipe(
        withLatestFrom(
          this.store.select(selectWidgetsLanguageId),
          this.store.select(selectCurrentObjectCacheEnabled),
        ),
        take(1),
        switchMap(
          ([widgetCopyWithData, objectToolsLanaguageId, cacheEnabled]) =>
            this.widgetApi.getWidgetPreview(
              widgetCopyWithData,
              this.contentFilters,
              objectToolsLanaguageId,
              cacheEnabled,
            ),
        ),
        tap((widgetPreview) => {
          this.widget = { ...this.widget, data: widgetPreview };
          this.widget.widget_type_id = vizType.widget_type_id;
          this.loadingWidgetPreview = false;
        }),
        catchError((error) => {
          this.logger.error(
            'WidgetSettingsDialogComponent',
            'selectVizType',
            'error',
            error,
          );
          this.loadingWidgetPreview = false;
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private updateWidgetData(widget: any, formData: any) {
    let widgetCopy;

    if (this.dialogData.massUpdate) {
      widgetCopy = {
        settings: {},
      };
    } else {
      widgetCopy = structuredClone(widget); //
    }

    // ! DUAL DATA PROCESSING STRATEGY:
    // ! Problem: Output settings need transformation from:
    // !   property_export_post_author_value: true  ->  {key: "export_post_author", selected: true}
    // !   From flat fields  ->  To nested objects and arrays
    // !
    // ! Solution: Use BOTH approaches:
    // ! 1) Filter out complex forms from automatic processing (prevent duplication)
    // ! 2) Handle complex forms with manual structured transformation

    // * Step 1: Let automatic collection handle simple form data (title, settings, etc.)
    const allFormData = this.tabManager.combineValues();

    widgetCopy.settings.map_zoom = this.latestZoom;
    widgetCopy.settings.map_center = this.latestCenter;

    // Ensure export_title is in the correct format
    if (Array.isArray(widgetCopy.settings.export_title)) {
      widgetCopy.settings.export_title = {};
    }

    // * Apply simple form data via dot notation (works great for flat structures)
    this.populateWidget(widgetCopy, allFormData);

    // * Step 2: Handle complex output settings transformation manually
    // * This creates proper nested structure: widgetCopy.formats = [{id, properties: [...]}]
    const outputSettingsFormats = this.getTransformedOutputSettingsFormats();
    // conflict
    widgetCopy.settings.output_settings = outputSettingsFormats;

    // * Step 3: Save "Override Output Settings" state from Radio Group
    const overrideState = this.getOverrideOutputSettingsState();

    if (!widgetCopy.settings) {
      widgetCopy.settings = {};
    }

    widgetCopy.settings.override_output_settings = overrideState;

    return widgetCopy;
  }

  // ! Read the radio group state from the output settings component
  // ! to save in widget.settings.override_output_settings
  private getOverrideOutputSettingsState(): boolean {
    // * Find the output settings tab component
    const outputSettingsTab = this.tabs.find(
      (tab) => tab.component === WidgetSettingsOutputSettingsComponent,
    );
    const outputTabComponent = this.tabManager.getTabComponent(
      outputSettingsTab.id,
    );

    if (
      outputTabComponent &&
      outputTabComponent instanceof WidgetSettingsOutputSettingsComponent
    ) {
      return outputTabComponent.overrideOutputSettings;
    }

    return false;
  }

  // ! MANUAL STRUCTURED TRANSFORMATION:
  private getTransformedOutputSettingsFormats(): any {
    // * Find the output settings tab component in the dynamic tab system
    const outputSettingsTab = this.tabs.find(
      (tab) => tab.component === WidgetSettingsOutputSettingsComponent,
    );
    const outputTabComponent = this.tabManager.getTabComponent(
      outputSettingsTab.id,
    );

    if (
      outputTabComponent &&
      outputTabComponent instanceof WidgetSettingsOutputSettingsComponent
    ) {
      // * Call the component's transformation method to get structured format data
      return outputTabComponent.getTransformedFormats();
    }
    this.logger.warn(
      'WidgetSettingsDialogComponent',
      'getTransformedFormats',
      'Output settings component not found or not initialized',
    );
    console.warn('Output settings component not found or not initialized');
    return [];
  }

  protected override performSave(
    formData: unknown,
  ): DialogCloseOptions<WidgetDialogOutput> {
    const widgetCopy = this.updateWidgetData(this.widget, formData);

    // ! the dialog closes optimistically below, so both writes run in a root
    // ! service the component no longer needs to be alive for
    if (this.dialogData.massUpdate) {
      // * Mass update: apply to all selected widgets.
      const widgetIds = this.widgets.map((w) => w.id);

      this.widgetPersistence.massUpdateWidgets(widgetIds, widgetCopy);
    } else {
      this.widgetPersistence.updateWidget(this.widget.id, widgetCopy);
    }

    return { reason: DialogCloseReason.Save, data: formData };
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    // * all valid, proceed with save
    const formData = this.tabManager.combineValues();
    const closeOptions = this.performSave(formData);

    // TODO: Optional - subscribe to success$ action and close dialog - discuss what approach we want?
    // TODO: if we close on success we need to disable buttons or give feedback to the user
    this.dirtyHandler.closeImmediately(closeOptions);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({ reason: DialogCloseReason.Cancel });
  }
}
